import React, { useState, useEffect, useRef } from 'react';
import { searchPosts, getTrendingHashtags, formatHashtag } from '../utils/hashtagUtils';
import './SearchComponent.css';

const SearchComponent = ({ posts: beams, onSearchResults, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [trendingTags, setTrendingTags] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const onSearchResultsRef = useRef(onSearchResults);

  // Update the ref when onSearchResults changes
  useEffect(() => {
    onSearchResultsRef.current = onSearchResults;
  }, [onSearchResults]);

  // Update trending hashtags when beams change
  useEffect(() => {
    if (beams && beams.length > 0) {
      const trending = getTrendingHashtags(beams, 10);
      setTrendingTags(trending);
    }
  }, [beams]);

  // Perform search when query or type changes
  useEffect(() => {
    if (searchQuery.trim()) {
      setIsSearching(true);
      const results = searchPosts(beams, searchQuery, searchType);
      setSearchResults(results);
      if (onSearchResultsRef.current) {
        onSearchResultsRef.current(results, searchQuery, searchType);
      }
      setIsSearching(false);
    } else {
      setSearchResults([]);
      if (onSearchResultsRef.current) {
        onSearchResultsRef.current(beams, '', searchType);
      }
    }
  }, [searchQuery, searchType, beams]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const results = searchPosts(beams, searchQuery, searchType);
      setSearchResults(results);
      if (onSearchResultsRef.current) {
        onSearchResultsRef.current(results, searchQuery, searchType);
      }
    }
  };

  const handleTrendingTagClick = (tag) => {
    setSearchQuery(`#${tag}`);
    setSearchType('hashtag');
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    if (onSearchResultsRef.current) {
      onSearchResultsRef.current(beams, '', searchType);
    }
  };

  return (
    <div className="search-component">
      <div className="search-header">
        <h3>🔍 Search Znoospace</h3>
        {onClose && (
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        )}
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearchSubmit} className="search-form">
        <div className="search-input-container">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search posts, hashtags, or users..."
            className="search-input"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="clear-search-btn"
            >
              ✕
            </button>
          )}
        </div>

        <div className="search-type-selector">
          <label>
            <input
              type="radio"
              value="all"
              checked={searchType === 'all'}
              onChange={(e) => setSearchType(e.target.value)}
            />
            All
          </label>
          <label>
            <input
              type="radio"
              value="hashtag"
              checked={searchType === 'hashtag'}
              onChange={(e) => setSearchType(e.target.value)}
            />
            Hashtags
          </label>
          <label>
            <input
              type="radio"
              value="keyword"
              checked={searchType === 'keyword'}
              onChange={(e) => setSearchType(e.target.value)}
            />
            Keywords
          </label>
          <label>
            <input
              type="radio"
              value="author"
              checked={searchType === 'author'}
              onChange={(e) => setSearchType(e.target.value)}
            />
            Authors
          </label>
        </div>

        <button type="submit" className="search-btn" disabled={!searchQuery.trim()}>
          Search
        </button>
      </form>

      {/* Search Results Summary */}
      {searchQuery && (
        <div className="search-results-summary">
          {isSearching ? (
            <p>Searching...</p>
          ) : (
            <p>
              Found {searchResults?.length || 0} result{(searchResults?.length || 0) !== 1 ? 's' : ''} 
              {searchType !== 'all' && ` in ${searchType}`} 
              for "{searchQuery}"
            </p>
          )}
        </div>
      )}

      {/* Trending Hashtags */}
      {!searchQuery && trendingTags.length > 0 && (
        <div className="trending-section">
          <h4>🔥 Trending Hashtags</h4>
          <div className="trending-tags">
            {trendingTags.map(({ tag, count }) => (
              <button
                key={tag}
                className="trending-tag"
                onClick={() => handleTrendingTagClick(tag)}
              >
                {formatHashtag(tag)}
                <span className="tag-count">{count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search Tips */}
      {!searchQuery && (
        <div className="search-tips">
          <h4>💡 Search Tips</h4>
          <ul>
            <li>Use <strong>#hashtag</strong> to find posts with specific tags</li>
            <li>Search by <strong>keywords</strong> to find relevant content</li>
            <li>Enter a <strong>wallet address</strong> to find posts by author</li>
            <li>Use <strong>"exact phrase"</strong> for precise matches</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchComponent;
